import { Scene } from 'phaser';
import { DataManagerAsset } from '../systems/DataManagerAsset';
import { initLocalization, t } from '../i18n/Localization';

interface LevelPreloaderData {
  levelId: string;
  levelNum: number;
  weapons?: {
    primary?: string | null;
    secondary?: string | null;
    special?: string | null;
  };
}

interface TextureInfo {
  key: string;
  path: string;
}

// Level data interfaces for deriving textures
interface LayoutObject {
  type: string;
}

interface LayoutTilemap {
  type: string;
}

interface LayoutLayer {
  objects: LayoutObject[];
  tilemaps?: LayoutTilemap[];
}

interface LayoutData {
  layers: LayoutLayer[];
}

interface EntityInstance {
  entityId: string;
  texture?: string;
}

interface BossInstance {
  entityId: string;
  texture?: string;
}

interface EntitiesData {
  entities: EntityInstance[];
  bosses?: BossInstance[];
}

// Level-specific overrides interface (for additional assets and entities)
interface LevelEntitiesOverrides {
  assets?: {
    atlas?: string[];
  };
  additions?: {
    entities?: Array<{ entityId?: string }>;
  };
}

// Animation data interface
interface AnimationEntry {
  key: string;
  frames: string[];
  frameRate: number;
  repeat: number;
}

interface EntityAnimationData {
  texture: string;
  animations: AnimationEntry[];
}

interface AnimationsData {
  _meta: { extractedAt: string; source: string };
  entities: Record<string, EntityAnimationData>;
}

/**
 * Merge animations-overrides.json into animations-c3data.json
 * (same logic used in Preloader)
 */
function mergeAnimationOverrides(
  animData: AnimationsData | undefined,
  overrides: AnimationsData | undefined
): AnimationsData | undefined {
  if (!animData || !overrides?.entities) return animData;

  for (const [entityName, entityOverride] of Object.entries(overrides.entities)) {
    if (!animData.entities[entityName]) {
      animData.entities[entityName] = entityOverride;
    } else {
      for (const overrideAnim of entityOverride.animations) {
        const existingIndex = animData.entities[entityName].animations
          .findIndex(a => a.key === overrideAnim.key);
        if (existingIndex >= 0) {
          animData.entities[entityName].animations[existingIndex] = overrideAnim;
        } else {
          animData.entities[entityName].animations.push(overrideAnim);
        }
      }
    }
  }

  return animData;
}

// Asset mapping interfaces
interface TextureDefinition {
  path: string;
  c3folder?: string;
  plugintype?: string;
  note?: string;
}

interface AssetsData {
  _meta?: { warning?: string; source?: string; generatedAt?: string; description?: string; note?: string };
  textures: Record<string, TextureDefinition>;
  globaltextures?: string[];
}

/**
 * Create a case-insensitive lookup map for animation entities
 * Maps lowercase keys to their actual data
 */
function createAnimLookup(animData: AnimationsData | undefined): Map<string, EntityAnimationData> {
  const lookup = new Map<string, EntityAnimationData>();
  if (!animData?.entities) return lookup;

  for (const [key, data] of Object.entries(animData.entities)) {
    lookup.set(key.toLowerCase(), data);
  }
  return lookup;
}

/**
 * Merge assets c3data with overrides to create texture path lookup.
 * Overrides take priority over c3data.
 */
function mergeAssetsData(
  c3data: AssetsData | undefined,
  overrides: AssetsData | undefined
): Record<string, string> {
  const result: Record<string, string> = {};

  // Add c3data textures
  if (c3data?.textures) {
    for (const [key, def] of Object.entries(c3data.textures)) {
      result[key.toLowerCase()] = def.path;
    }
  }

  // Apply overrides (take priority)
  if (overrides?.textures) {
    for (const [key, def] of Object.entries(overrides.textures)) {
      result[key.toLowerCase()] = def.path;
    }
  }

  return result;
}

/**
 * Merge global textures from c3data and overrides.
 */
function mergeGlobalTextures(
  c3data: AssetsData | undefined,
  overrides: AssetsData | undefined
): Set<string> {
  const result = new Set<string>();

  if (c3data?.globaltextures) {
    for (const tex of c3data.globaltextures) {
      result.add(tex.toLowerCase());
    }
  }

  if (overrides?.globaltextures) {
    for (const tex of overrides.globaltextures) {
      result.add(tex.toLowerCase());
    }
  }

  return result;
}

// Module-level texture path lookup (populated from JSON in create())
let texturePathLookup: Record<string, string> = {};

/**
 * Get texture path for a type. Falls back to spritesheets for animated entities.
 */
function getTexturePath(key: string): string | null {
  const normalizedKey = key.toLowerCase();

  // Check lookup from JSON data
  if (texturePathLookup[normalizedKey]) {
    return texturePathLookup[normalizedKey];
  }

  // Unknown - will be checked against animation data in create()
  // Return null to indicate it might be an atlas
  return null;
}

/**
 * Collect base entity/object IDs from layout and entities data.
 * Does NOT resolve dependencies - that's handled by DataManagerAsset.
 */
function collectBaseIds(
  layout: LayoutData | undefined,
  entities: EntitiesData | undefined,
  levelOverrides?: LevelEntitiesOverrides
): { layoutIds: string[]; entityIds: string[]; additionalAtlas: string[] } {
  const layoutIds: string[] = [];
  const entityIds: string[] = [];
  const additionalAtlas: string[] = [];

  // From layout (backgrounds, environment objects, tilemaps)
  if (layout?.layers) {
    for (const layer of layout.layers) {
      for (const obj of layer.objects) {
        layoutIds.push(obj.type.toLowerCase());
      }
      if (layer.tilemaps) {
        for (const tm of layer.tilemaps) {
          layoutIds.push(tm.type.toLowerCase());
        }
      }
    }
  }

  // From entities (spawnable entities)
  if (entities?.entities) {
    for (const entity of entities.entities) {
      if (!entity.entityId) {
        console.error('[LevelPreloader] Entity missing entityId:', entity);
        continue;
      }
      entityIds.push(entity.entityId.toLowerCase());
    }
  }

  // From bosses
  if (entities?.bosses) {
    for (const boss of entities.bosses) {
      if (!boss.entityId) {
        console.error('[LevelPreloader] Boss missing entityId:', boss);
        continue;
      }
      entityIds.push(boss.entityId.toLowerCase());
    }
  }

  // From level-specific overrides: additional entities (e.g., soniccrystal hills)
  if (levelOverrides?.additions?.entities) {
    for (const entity of levelOverrides.additions.entities) {
      if (entity.entityId) {
        entityIds.push(entity.entityId.toLowerCase());
      }
    }
  }

  // From level-specific overrides: additional assets (e.g., boss portraits)
  // These are direct atlas textures, NOT entities - don't pass through entity resolver
  if (levelOverrides?.assets?.atlas) {
    for (const atlas of levelOverrides.assets.atlas) {
      additionalAtlas.push(atlas.toLowerCase());
    }
  }

  return { layoutIds, entityIds, additionalAtlas };
}

/**
 * Convert texture keys to TextureInfo with paths
 */
function toTextureInfoArray(keys: Iterable<string>): TextureInfo[] {
  const result: TextureInfo[] = [];
  for (const key of keys) {
    const path = getTexturePath(key);
    if (path) {
      result.push({ key, path });
    } else {
      // No explicit path - assume spritesheet atlas
      result.push({ key, path: `game/spritesheets/${key}.png` });
    }
  }
  return result;
}

/**
 * LevelPreloader Scene
 *
 * Intermediate scene that loads level-specific assets before starting the game.
 * This allows for dynamic per-level asset loading instead of loading everything upfront.
 */
export class LevelPreloader extends Scene {
  private levelId: string = 'level1';
  private levelNum: number = 1;
  private weapons: LevelPreloaderData['weapons'] = {};
  private derivedTextures: TextureInfo[] = [];
  private loadingText!: Phaser.GameObjects.Text;
  private progressText!: Phaser.GameObjects.Text;

  constructor() {
    super('LevelPreloader');
  }

  init(data: LevelPreloaderData): void {
    this.levelId = data.levelId || 'level1';
    this.levelNum = data.levelNum || 1;
    this.weapons = data.weapons || {};
  }

  preload(): void {
    this.createLoadingUI();

    // Ensure DataManagerAsset JSONs are loaded (may already be cached from Preloader)
    DataManagerAsset.preload(this);

    // Load asset path mappings (texture key → file path)
    this.load.json('assets-c3data', '/assets/data/assets-c3data.json');
    this.load.json('assets-overrides', '/assets/data/assets-overrides.json');

    // Load layout and entities data for deriving textures (SSOT - no manifest needed)
    this.load.json(`${this.levelId}-layout-c3data`, `/assets/levels/${this.levelId}-layout-c3data.json`);
    this.load.json(`${this.levelId}-entities-c3data`, `/assets/levels/${this.levelId}-entities-c3data.json`);

    // Load level-specific overrides (layout + entities)
    this.load.json(`${this.levelId}-layout-overrides`, `/assets/levels/${this.levelId}-layout-overrides.json`);
    this.load.json(`${this.levelId}-entities-overrides`, `/assets/levels/${this.levelId}-entities-overrides.json`);
  }

  create(): void {
    // Initialize DataManagerAsset (loads and merges c3data + overrides)
    DataManagerAsset.load(this);

    // Initialize texture path lookup from JSON data
    const assetsC3Data = this.cache.json.get('assets-c3data') as AssetsData | undefined;
    const assetsOverrides = this.cache.json.get('assets-overrides') as AssetsData | undefined;
    texturePathLookup = mergeAssetsData(assetsC3Data, assetsOverrides);
    // console.log(`[LevelPreloader] Loaded ${Object.keys(texturePathLookup).length} texture path mappings`);

    // Collect base IDs from level data
    const layoutData = this.cache.json.get(`${this.levelId}-layout-c3data`) as LayoutData | undefined;
    const entitiesData = this.cache.json.get(`${this.levelId}-entities-c3data`) as EntitiesData | undefined;
    const levelOverridesData = this.cache.json.get(`${this.levelId}-entities-overrides`) as LevelEntitiesOverrides | undefined;
    const { layoutIds, entityIds, additionalAtlas } = collectBaseIds(layoutData, entitiesData, levelOverridesData);

    // Use DataManagerAsset to resolve ALL entity dependencies (including spawnOnTransform)
    const resolved = DataManagerAsset.resolveEntities(entityIds);

    // Combine layout IDs (simple textures) with resolved entity atlas + additional atlas
    // additionalAtlas are direct textures (e.g., boss portraits) that bypass entity resolution
    const allTextureKeys = new Set<string>([...layoutIds, ...resolved.atlas, ...additionalAtlas]);
    // console.log(`[LevelPreloader] Resolved ${entityIds.length} entities → ${resolved.atlas.size} atlas textures`);

    // Convert to TextureInfo array
    this.derivedTextures = toTextureInfoArray(allTextureKeys);

    if (this.derivedTextures.length === 0) {
      this.startGame();
      return;
    }

    // Filter out already loaded textures
    const texturesToLoad = this.derivedTextures.filter(
      (tex) => !this.textures.exists(tex.key)
    );

    if (texturesToLoad.length === 0) {
      this.startGame();
      return;
    }

    // Get animation data for determining load type (case-insensitive lookup)
    const animData = this.cache.json.get('animations-c3data') as AnimationsData | undefined;
    const overrides = this.cache.json.get('animations-overrides') as AnimationsData | undefined;
    const mergedAnimData = mergeAnimationOverrides(animData, overrides);
    const animLookup = createAnimLookup(mergedAnimData);

    // Update UI for loading phase
    this.loadingText.setText(t('level_preloader.loading').replace('{level}', this.levelId));

    // Set up progress tracking
    this.load.on('progress', (value: number) => {
      const percent = Math.round(value * 100);
      this.progressText.setText(`${percent}%`);
      this.updateProgressBar(value);
    });

    this.load.on('complete', () => {
      this.createLevelAnimations(animLookup, texturesToLoad);
      this.startGame();
    });

    // Queue texture loading - atlas or image based on animation data
    this.load.setPath('../assets');
    for (const tex of texturesToLoad) {
      // Check if this texture has animation data (case-insensitive)
      const entityData = animLookup.get(tex.key.toLowerCase());
      if (entityData) {
        // Load as atlas - use the texture name from animation data
        this.load.atlas(
          tex.key,
          `game/spritesheets/${entityData.texture}.png`,
          `game/spritesheets/${entityData.texture}.json`
        );
      } else {
        // Load as regular image
        this.load.image(tex.key, tex.path);
      }
    }

    // Start loading
    this.load.start();
  }

  /**
   * Create animations for level-specific entities
   */
  private createLevelAnimations(
    animLookup: Map<string, EntityAnimationData>,
    textures: TextureInfo[]
  ): void {
    if (animLookup.size === 0) return;

    for (const tex of textures) {
      // Case-insensitive lookup
      const data = animLookup.get(tex.key.toLowerCase());
      if (!data) continue;

      // Verify texture was loaded (use tex.key, the actual loaded key)
      if (!this.textures.exists(tex.key)) {
        continue;
      }

      for (const anim of data.animations) {
        // Create animation key using loaded texture key (lowercase)
        // e.g., plasmastore_default
        const animKey = `${tex.key}_${anim.key.split('_').slice(1).join('_')}`;

        // Skip if animation already exists
        if (this.anims.exists(animKey)) {
          continue;
        }

        // Build frames array - use tex.key and normalize frame names
        // Frame names in JSON are like "plasmastore_default_0"
        // We map them to use tex.key: "plasmastore" and keep original frame reference
        const frames = anim.frames.map(f => ({ key: tex.key, frame: f }));

        this.anims.create({
          key: animKey,
          frames: frames,
          frameRate: anim.frameRate,
          repeat: anim.repeat,
        });
      }
    }
  }

  private createLoadingUI(): void {
    initLocalization(this);
    const { width, height } = this.scale;
    const centerX = width / 2;
    const centerY = height / 2;

    // Dark blue gradient background (matching Preloader style)
    const graphics = this.add.graphics();
    const colorTop = 0x2d5a7b;
    const colorBottom = 0x0d1f33;

    const steps = 32;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const r = Math.floor(((colorTop >> 16) & 0xff) * (1 - t) + ((colorBottom >> 16) & 0xff) * t);
      const g = Math.floor(((colorTop >> 8) & 0xff) * (1 - t) + ((colorBottom >> 8) & 0xff) * t);
      const b = Math.floor((colorTop & 0xff) * (1 - t) + (colorBottom & 0xff) * t);
      const color = (r << 16) | (g << 8) | b;

      graphics.fillStyle(color, 1);
      graphics.fillRect(0, (height / steps) * i, width, height / steps + 1);
    }

    // Progress bar dimensions
    const barWidth = 460;
    const barHeight = 32;
    const barX = centerX - barWidth / 2;
    const barY = centerY + 20 - barHeight / 2;
    const radius = 12;

    // Progress bar background (dark with rounded corners)
    const barBg = this.add.graphics();
    barBg.fillStyle(0x1a1a2e, 1);
    barBg.fillRoundedRect(barX - 4, barY - 4, barWidth + 8, barHeight + 8, radius);
    barBg.lineStyle(2, 0x4a9c6d, 1);
    barBg.strokeRoundedRect(barX - 4, barY - 4, barWidth + 8, barHeight + 8, radius);

    // Store bar dimensions for progress updates
    this.data.set('barX', barX);
    this.data.set('barY', barY);
    this.data.set('barWidth', barWidth);
    this.data.set('barHeight', barHeight);
    this.data.set('barRadius', radius);

    // Initial progress bar fill (empty)
    const barFill = this.add.graphics();
    barFill.setName('barFill');

    // Loading text
    this.loadingText = this.add.text(
      centerX,
      centerY - 40,
      t('level_preloader.preparing').replace('{level}', `${this.levelNum}`),
      {
      fontFamily: 'Arial',
      fontSize: '28px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Progress percentage
    this.progressText = this.add.text(centerX, centerY + 60, '0%', {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#88ff88',
    }).setOrigin(0.5);
  }

  private updateProgressBar(progress: number): void {
    const barFill = this.children.getByName('barFill') as Phaser.GameObjects.Graphics;
    if (!barFill) return;

    const barX = this.data.get('barX') as number;
    const barY = this.data.get('barY') as number;
    const barWidth = this.data.get('barWidth') as number;
    const barHeight = this.data.get('barHeight') as number;
    const radius = this.data.get('barRadius') as number;

    barFill.clear();
    barFill.fillStyle(0x4CAF50, 1);
    const fillWidth = Math.max(barHeight, barWidth * progress);
    barFill.fillRoundedRect(barX, barY, fillWidth, barHeight, radius);
  }

  private startGame(): void {
    // Small delay for visual feedback
    this.time.delayedCall(200, () => {
      this.scene.start('Game', {
        levelId: this.levelId,
        levelNum: this.levelNum,
        weapons: this.weapons,
      });
    });
  }
}
